import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Badge } from 'react-bootstrap';
import {
  useGetResourceAdminPagesQuery,
  useCreateResourceGroupMutation,
  useUpdateResourceGroupMutation,
  useDeleteResourceGroupMutation,
  useReorderResourceGroupsMutation,
  useCreateResourcePageMutation,
  useUpdateResourcePageMutation,
  useDeleteResourcePageMutation,
  useReorderResourcePagesMutation,
  useCreateResourceLinkMutation,
  useGetResourceUploadTicketMutation,
  useCommitResourceUploadMutation,
  useUpdateResourceItemMutation,
  useDeleteResourceItemMutation,
  useReorderResourceItemsMutation,
  useGetResourceItemFileUrlMutation,
} from '../../app/api';
// The shared employee-admin styling kit. It lives under entrepreneur/ for
// historical reasons; AdminPage is the same shell with a section label, so
// nothing here reads as though Marketing sits under Entrepreneur.
import { brand, cardStyle, headStyle, AdminPage } from '../entrepreneur/common';
import { RICH_TEXT_HINT } from '../richtext';
import c2pCentralLoginLink from '../../components/c2pCentralLink';

/* Resource Library — the internal reference pages, MARKETING authoring view.
 *
 * NOT Marketing Resources (routes/marketing/Resources.jsx), which Marketing
 * also own and which sits next to this in the same Admin group. That one is
 * the B2B collateral library: marketing_categories, the pca-marketing
 * top-level prefixes, customer-facing by default. This one is internal
 * reference pages that a checkbox can additionally expose to the firms. Same
 * team, same bucket, different tables and different default audience.
 *
 * Two levels of content, plus one for navigation:
 *
 *   folder one expandable entry in the sidebar, holding pages. A name, an icon
 *          and a position and nothing else — no URL, no storage, no publish
 *          flag. OPTIONAL: a page in no folder is listed directly under
 *          Resource Library, which is where every page sat before folders
 *          existed.
 *   page   one sub-page of Desk → Resource Library (its own sidebar entry and
 *          its own URL, /resources/<slug>)
 *   item   one row in that page's list: a title, a description and one thing
 *          to open
 *
 * A FOLDER HAS NO AUDIENCE OF ITS OWN, which is why this screen offers no
 * checkboxes on one. It is shown to whoever can see a page inside it and
 * disappears otherwise, so an empty folder is invisible to everybody and a
 * folder of internal pages never reaches a firm — not even its name. Putting a
 * publish flag on a folder would instead let a page be ticked "visible to
 * employees" and still be unreachable, with this screen calling it published.
 *
 * TWO AUDIENCES, AND AN EARLY-ACCESS STEP — three checkboxes, but not three of
 * the same thing:
 *
 *   Visible to employees      the Desk sidebar, everyone
 *   Visible to entrepreneurs  every B2B firm's own Resource Library
 *   Visible to beta testers   employees holding BETA, BEFORE the first one
 *
 * The first two are independent AUDIENCES, and the second is OFF for a new page
 * and for every page that predates it — a page reaches an external firm because
 * someone ticked a box naming that audience, never as a side effect of
 * publishing it internally.
 *
 * The third is not an audience at all: it WIDENS the employee one early. BETA is
 * an employee role (the firm's standing early-access group), so the progression
 * is beta → employees, and ticking it alongside "Visible to employees" is inert
 * because beta testers are employees too. The form says which of those two
 * states it is in, since they look identical on screen. It cannot reach a firm
 * under any combination — the B2B read path does not look at the flag.
 *
 * See docs/resource-library.md.
 *
 * A page's ADDRESS is fixed when it is created and shown here as read-only next
 * to the name, because that is the one thing on this page a rename cannot
 * change: it is both the URL people bookmark and the folder its uploads are
 * already stored under. Showing it is what stops that being a surprise later.
 *
 * THE ITEM TYPE IS NOT A FIELD. There are two ways to add an item — paste a URL
 * or upload a file — and the server decides which of the four kinds the result
 * is: a YouTube/Vimeo URL becomes an inline player (EMBED) and anything else a
 * link (LINK); an uploaded video becomes an inline player (VIDEO) and anything
 * else a document (FILE). A dropdown would let someone assert a type that
 * disagrees with what they actually supplied.
 *
 * Files go browser → S3 directly (a presigned POST ticket from the API), never
 * through the API, so a 200 MB recording is not blocked by the 26 MB
 * request-body cap on the nginx in front of us.
 */

// Bootstrap icons offered for a page. A short curated list rather than a
// free-text field — the icon shows up in everyone's sidebar, so it should be
// one of a handful that look right there.
const ICON_CHOICES = [
  { value: 'collection',          label: 'Collection' },
  { value: 'file-earmark-text',   label: 'Document' },
  { value: 'book',                label: 'Book / guide' },
  { value: 'play-btn',            label: 'Video' },
  { value: 'mortarboard',         label: 'Training' },
  { value: 'cpu',                 label: 'Tech' },
  { value: 'tools',               label: 'Tools' },
  { value: 'shield-lock',         label: 'Security' },
  { value: 'question-circle',     label: 'How-to / FAQ' },
  { value: 'people-fill',         label: 'People' },
  { value: 'building',            label: 'Office' },
  { value: 'graph-up-arrow',      label: 'Reports' },
];

const inputStyle = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};
const areaStyle = { ...inputStyle, minHeight: 90, fontFamily: 'inherit', lineHeight: 1.45 };
const btnPrimary = {
  background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
  padding: '6px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
};
const btnPlain = {
  background: 'white', color: brand.charcoal, border: `1px solid ${brand.softGray}`,
  borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
};
const btnTiny = { ...btnPlain, padding: '2px 8px', fontSize: '0.75rem' };

const fmtSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const errText = (e, fallback) => e?.data?.detail || e?.detail || e?.message || fallback;

// What each kind is called on screen, and how it is marked. The server sets
// `kind`; this is only the vocabulary for showing it back.
const KIND_META = {
  FILE:  { label: 'Document',      icon: 'file-earmark-arrow-down' },
  VIDEO: { label: 'Video (hosted here)', icon: 'camera-video' },
  LINK:  { label: 'Link',          icon: 'link-45deg' },
  EMBED: { label: 'Video (embedded)',    icon: 'play-btn' },
};

/* Send the bytes straight to S3 with the ticket the API minted. The presigned
 * POST's own conditions (bucket, key, content type, size range) are what
 * enforce the rules — this function just assembles the form in the order S3
 * requires: every field first, `file` LAST. */
async function putToS3(ticket, file) {
  const form = new FormData();
  Object.entries(ticket.fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', file);
  const res = await fetch(ticket.url, { method: 'POST', body: form });
  if (!res.ok) {
    // S3 answers with an XML error body; its <Message> is the useful part and
    // the status alone ("403") tells the user nothing actionable.
    const body = await res.text().catch(() => '');
    const message = /<Message>([^<]+)<\/Message>/.exec(body)?.[1];
    throw new Error(message || `Upload failed (HTTP ${res.status}).`);
  }
}

// Reorder helper shared by pages and items: swap with a neighbour and send the
// WHOLE list back, so the server writes evenly-spaced positions rather than us
// guessing at them.
const swapped = (ids, id, delta) => {
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  const next = [...ids];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

function PageForm({ draft, onChange, groups }) {
  const set = (k, v) => onChange({ ...draft, [k]: v });
  return (
    <>
      <input style={{ ...inputStyle, marginBottom: '0.5rem' }} placeholder="Page name — e.g. Salesforce How-Tos"
        value={draft.name} onChange={(e) => set('name', e.target.value)} />
      <input style={{ ...inputStyle, marginBottom: '0.5rem' }} placeholder="Short description (optional) — one line under the title"
        value={draft.description} onChange={(e) => set('description', e.target.value)} />
      <textarea style={{ ...areaStyle, marginBottom: '0.2rem' }}
        placeholder="Intro shown above the list (optional)"
        value={draft.intro} onChange={(e) => set('intro', e.target.value)} />
      <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginBottom: '0.5rem' }}>{RICH_TEXT_HINT}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Which sidebar folder this page sits in, or none — in which case it
            stays a direct entry under Resource Library, where every page was
            before folders existed. Unlike the address below, this is free to
            change later: a folder owns no URL and no storage, so moving a page
            between folders breaks no bookmark and moves no file. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem' }}>
          Folder:
          <select style={{ ...inputStyle, width: 'auto' }}
            value={draft.group_id == null ? '' : String(draft.group_id)}
            onChange={(e) => set('group_id', e.target.value === '' ? null : Number(e.target.value))}>
            <option value="">None — top level</option>
            {groups.map((g) => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem' }}>
          Icon:
          <select style={{ ...inputStyle, width: 'auto' }} value={draft.icon}
            onChange={(e) => set('icon', e.target.value)}>
            {/* Honest again now that the sidebar no longer substitutes a
                document icon: no icon means no icon, and the one you pick
                shows on the page's own header rather than in the menu. */}
            <option value="">None</option>
            {ICON_CHOICES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          {draft.icon && <i className={`bi bi-${draft.icon}`} style={{ color: brand.goldenrod, fontSize: '1.1rem' }} />}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.published} onChange={(e) => set('published', e.target.checked)} />
          Visible to employees
        </label>
        {/* The second audience. Independent of the first, and off by default —
            ticking it puts this page in every entrepreneur firm's hub, next to
            the Marketing sections, so it is a decision rather than a
            consequence of finishing the page. A page can be ticked for the
            firms ONLY, which is how you write something for them without
            adding a row to every employee's sidebar. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.b2b_published} onChange={(e) => set('b2b_published', e.target.checked)} />
          Visible to entrepreneurs
        </label>
        {/* Early access, and NOT a third audience — which is why it reads as a
            staging step rather than a group name. It widens the employee
            audience to the BETA role holders while "Visible to employees" is
            still off; ticking both is inert, because anyone with BETA is
            already an employee. It can never reach a firm: BETA is an employee
            role and the B2B read path does not look at this flag. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.82rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!draft.beta_published} onChange={(e) => set('beta_published', e.target.checked)} />
          Visible to beta testers
        </label>
      </div>
      {draft.b2b_published && (
        <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginTop: '0.4rem' }}>
          <i className="bi bi-people me-1" style={{ color: brand.goldenrod }} />
          External firms will see this page and everything on it. Hidden items
          stay hidden from them too.
        </div>
      )}
      {/* Say which of the two states the beta tick is actually in, because they
          look identical on the form and do completely different things. */}
      {draft.beta_published && !draft.published && (
        <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginTop: '0.4rem' }}>
          <i className="bi bi-hourglass-split me-1" style={{ color: brand.goldenrod }} />
          Pre-release: only employees with the BETA role will see this. Tick
          &quot;Visible to employees&quot; when it is ready for everyone.
        </div>
      )}
      {draft.beta_published && draft.published && (
        <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginTop: '0.4rem' }}>
          <i className="bi bi-info-circle me-1" style={{ color: brand.goldenrod }} />
          This page is already visible to all employees, so the beta tick is
          doing nothing — beta testers are employees too. Harmless to leave on.
        </div>
      )}
    </>
  );
}
PageForm.propTypes = {
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  groups: PropTypes.array,
};
PageForm.defaultProps = { groups: [] };

/* One item. Uploads show their size and an "Open"; links show (and can change)
 * their URL — and changing it re-decides whether the row is a plain link or an
 * inline video, which is why the hint under the field says so. */
function ItemRow({ item, pages, first, last, onMove, onSaved, onError }) {
  const [update, updateState] = useUpdateResourceItemMutation();
  const [remove] = useDeleteResourceItemMutation();
  const [getFileUrl] = useGetResourceItemFileUrlMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: item.title || '',
    description: item.description || '',
    url: item.url || '',
  });

  const isUpload = item.kind === 'FILE' || item.kind === 'VIDEO';
  const meta = KIND_META[item.kind] || KIND_META.FILE;

  const save = async () => {
    const body = { title: draft.title.trim(), description: draft.description || null };
    if (!isUpload) body.url = draft.url.trim();
    try {
      await update({ id: item.id, body }).unwrap();
      setEditing(false);
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not save that item.')); }
  };

  const togglePublished = async () => {
    try {
      await update({ id: item.id, body: { published: !item.published } }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not change visibility.')); }
  };

  // Only offered for links and embeds: an uploaded file's key encodes its page,
  // so the server refuses the move and the control would be a dead end.
  const moveToPage = async (value) => {
    try {
      await update({ id: item.id, body: { page_id: Number(value) } }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not move that item.')); }
  };

  const del = async () => {
    const what = isUpload ? 'file (it will be removed from S3)' : 'link';
    if (!window.confirm(`Delete "${item.title}"? This deletes the ${what} and cannot be undone.`)) return;
    try {
      await remove({ id: item.id }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not delete that item.')); }
  };

  const openUpload = async () => {
    try {
      const res = await getFileUrl({ id: item.id, disposition: 'inline' }).unwrap();
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) { onError(errText(e, 'Could not open that file.')); }
  };

  return (
    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0', borderTop: `1px solid ${brand.softGray}` }}>
      {/* Reorder handles. The order set here is the order employees see. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
        <button type="button" style={{ ...btnTiny, opacity: first ? 0.35 : 1 }} disabled={first}
          onClick={() => onMove(item.id, -1)} aria-label="Move up" title="Move up">
          <i className="bi bi-chevron-up" />
        </button>
        <button type="button" style={{ ...btnTiny, opacity: last ? 0.35 : 1 }} disabled={last}
          onClick={() => onMove(item.id, 1)} aria-label="Move down" title="Move down">
          <i className="bi bi-chevron-down" />
        </button>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <>
            <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Title"
              value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Description (optional)"
              value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            {!isUpload && (
              <>
                <input style={{ ...inputStyle, marginBottom: '0.2rem' }} placeholder="https://…"
                  value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
                <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginBottom: '0.4rem' }}>
                  A YouTube or Vimeo URL becomes a video that plays on the page; anything
                  else opens in a new tab.
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button type="button" style={btnPrimary} onClick={save}
                disabled={updateState.isLoading || !draft.title.trim()}>
                {updateState.isLoading ? 'Saving…' : 'Save'}
              </button>
              <button type="button" style={btnPlain} onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
              <i className={`bi bi-${meta.icon}`} style={{ color: brand.tealBlue }} />
              <span style={{ fontWeight: 700, color: brand.deepBlue }}>{item.title}</span>
              {!item.published && <Badge bg="secondary" style={{ fontWeight: 600 }}>Hidden</Badge>}
              <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
                {isUpload
                  ? [meta.label, item.filename, fmtSize(item.content_size)].filter(Boolean).join(' · ')
                  : meta.label}
              </span>
            </div>
            {item.description && (
              <div style={{ color: brand.charcoal, fontSize: '0.82rem', marginTop: '0.2rem' }}>{item.description}</div>
            )}
            {!isUpload && (
              // The row shows the URL exactly as it was entered; only where the
              // click GOES is rewritten, so C2P Central course links behave here
              // the same as they do for a reader.
              <a href={c2pCentralLoginLink(item.url)} target="_blank" rel="noopener noreferrer"
                style={{ color: brand.tealBlue, fontSize: '0.75rem', wordBreak: 'break-all' }}>
                {item.url}
              </a>
            )}
            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" style={btnTiny} onClick={() => setEditing(true)}>Edit</button>
              <button type="button" style={btnTiny} onClick={togglePublished}>
                {item.published ? 'Hide' : 'Show'}
              </button>
              {isUpload && <button type="button" style={btnTiny} onClick={openUpload}>Open</button>}
              {/* Uploads are missing this control on purpose — see moveToPage. */}
              {!isUpload && pages.length > 1 && (
                <select style={{ ...btnTiny, padding: '2px 6px' }} value={item.page_id}
                  aria-label="Move to page" title="Move to another page"
                  onChange={(e) => moveToPage(e.target.value)}>
                  {pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
              <button type="button" style={{ ...btnTiny, color: brand.red, borderColor: brand.red }} onClick={del}>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
ItemRow.propTypes = {
  item: PropTypes.object.isRequired,
  pages: PropTypes.array,
  first: PropTypes.bool,
  last: PropTypes.bool,
  onMove: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
ItemRow.defaultProps = { pages: [], first: false, last: false };

/* The "add something here" panel. Two ways in — paste a URL, or upload a file —
 * because those are the two things a person actually has in hand. Which of the
 * four kinds the result becomes is the server's call. */
function AddItem({ page, onSaved, onError }) {
  const [mode, setMode] = useState(null);      // null | 'file' | 'link'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const [createLink] = useCreateResourceLinkMutation();
  const [getTicket] = useGetResourceUploadTicketMutation();
  const [commit] = useCommitResourceUploadMutation();

  const reset = () => {
    setMode(null); setTitle(''); setDescription(''); setUrl('');
    setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submitLink = async () => {
    setBusy(true);
    try {
      await createLink({
        page_id: page.id, title: title.trim(),
        url: url.trim(), description: description || null,
      }).unwrap();
      reset();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not add that link.')); }
    finally { setBusy(false); }
  };

  const submitFile = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { onError('Choose a file first.'); return; }
    setBusy(true);
    try {
      setProgress('Preparing…');
      const ticket = await getTicket({
        page_id: page.id, filename: file.name,
        size_bytes: file.size, content_type: file.type || null,
      }).unwrap();

      setProgress(`Uploading ${fmtSize(file.size)}…`);
      await putToS3(ticket, file);

      // Only now does a row exist. If commit fails the object is orphaned in S3
      // rather than catalogued — the message says so, because the fix is to try
      // again (a second upload makes a new key) not to hunt for a half-made row.
      setProgress('Saving…');
      await commit({
        page_id: page.id, s3_key: ticket.s3_key,
        title: (title.trim() || file.name), filename: file.name,
        description: description || null, content_type: ticket.content_type,
      }).unwrap();
      reset();
      await onSaved();
    } catch (e) {
      onError(errText(e, 'Upload failed.'));
      setProgress(null);
    } finally { setBusy(false); }
  };

  if (!mode) {
    return (
      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
        <button type="button" style={btnPlain} onClick={() => setMode('link')}>
          <i className="bi bi-link-45deg me-1" />Add link or video
        </button>
        <button type="button" style={btnPlain} onClick={() => setMode('file')}>
          <i className="bi bi-upload me-1" />Upload document or video
        </button>
      </div>
    );
  }

  const submit = mode === 'file' ? submitFile : submitLink;
  const disabled = busy || (mode === 'link' && (!title.trim() || !url.trim()));

  return (
    <div style={{ background: '#fbfaf8', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.8rem', marginTop: '0.6rem' }}>
      <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
        {mode === 'file' ? `Upload to ${page.name}` : `Add a link or video to ${page.name}`}
      </div>
      {mode === 'file' && (
        <input ref={fileRef} type="file" style={{ ...inputStyle, marginBottom: '0.4rem', padding: '5px 8px' }} />
      )}
      <input style={{ ...inputStyle, marginBottom: '0.4rem' }}
        placeholder={mode === 'file' ? 'Title (defaults to the file name)' : 'Title'}
        value={title} onChange={(e) => setTitle(e.target.value)} />
      <input style={{ ...inputStyle, marginBottom: '0.4rem' }} placeholder="Description (optional)"
        value={description} onChange={(e) => setDescription(e.target.value)} />
      {mode === 'link' && (
        <>
          <input style={{ ...inputStyle, marginBottom: '0.2rem' }} placeholder="https://…"
            value={url} onChange={(e) => setUrl(e.target.value)} />
          <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginBottom: '0.4rem' }}>
            <i className="bi bi-info-circle me-1" />
            Paste a YouTube or Vimeo link and it plays on the page. Anything else
            becomes a link that opens in a new tab.
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={btnPrimary} disabled={disabled} onClick={submit}>
          {busy ? (progress || 'Working…') : (mode === 'file' ? 'Upload' : 'Add')}
        </button>
        <button type="button" style={btnPlain} onClick={reset} disabled={busy}>Cancel</button>
        {mode === 'file' && !busy && (
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            An mp4, mov, m4v or webm plays on the page. Goes to{' '}
            <code>pca-marketing/resource-library/{page.slug}/</code>
          </span>
        )}
      </div>
    </div>
  );
}
AddItem.propTypes = {
  page: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

function PageCard({ page, pages, groups, first, last, onMovePage, onSaved, onError }) {
  const [update, updateState] = useUpdateResourcePageMutation();
  const [remove] = useDeleteResourcePageMutation();
  const [reorderItems] = useReorderResourceItemsMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: page.name || '', description: page.description || '',
    intro: page.intro || '', icon: page.icon || '', published: !!page.published,
    b2b_published: !!page.b2b_published,
    beta_published: !!page.beta_published,
    // `?? null` and not `|| null`: group_id 0 is not a real id, but coercing an
    // absent one to a falsy string would put the select on the wrong option.
    group_id: page.group_id ?? null,
  });

  const items = page.items || [];

  const save = async () => {
    try {
      await update({
        id: page.id,
        body: {
          name: draft.name.trim(), description: draft.description || null,
          intro: draft.intro || null, icon: draft.icon || null,
          // Explicit null is the "take it out of the folder" case, and the
          // server tells it apart from an absent key — so this always sends it.
          group_id: draft.group_id ?? null,
          published: !!draft.published,
          b2b_published: !!draft.b2b_published,
          beta_published: !!draft.beta_published,
        },
      }).unwrap();
      setEditing(false);
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not save that page.')); }
  };

  const del = async () => {
    if (!window.confirm(`Delete the "${page.name}" page?`)) return;
    try {
      await remove({ id: page.id }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not delete that page.')); }
  };

  const moveItem = async (itemId, delta) => {
    const ids = swapped(items.map((i) => i.id), itemId, delta);
    if (!ids) return;
    try {
      await reorderItems({ ordered_ids: ids }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not reorder those items.')); }
  };

  return (
    <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
      <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        {page.icon && <i className={`bi bi-${page.icon}`} style={{ color: brand.goldenrod }} />}
        <span>{page.name}</span>
        <Badge bg="secondary" style={{ fontWeight: 600 }}>
          {items.length} item{items.length === 1 ? '' : 's'}
        </Badge>
        {/* Two audiences, so "Hidden" alone would no longer say from whom. The
            employee flag is only worth a badge when it is OFF (the usual state
            is on); the entrepreneur flag only when it is ON, because that is
            the one that puts the page in front of a customer.

            The beta tier splits the "off" case in two, and the distinction is
            the whole point of the tier: a beta-only page is NOT hidden from
            employees, it is hidden from all but one group of them. Badging it
            "Hidden from employees" would have someone chasing why a colleague
            can see a page the screen calls hidden. */}
        {!page.published && page.beta_published && (
          <Badge bg="warning" text="dark" style={{ fontWeight: 600 }}>
            <i className="bi bi-hourglass-split me-1" />Beta testers only
          </Badge>
        )}
        {!page.published && !page.beta_published && (
          <Badge bg="warning" text="dark" style={{ fontWeight: 600 }}>Hidden from employees</Badge>
        )}
        {page.b2b_published && (
          <Badge bg="info" text="dark" style={{ fontWeight: 600 }}>
            <i className="bi bi-people me-1" />Entrepreneurs
          </Badge>
        )}
        {/* The address is fixed at creation — it is the bookmarked URL and the
            S3 folder — so it is shown rather than left to be discovered. */}
        <code style={{ color: brand.charcoal, fontSize: '0.72rem' }}>/resources/{page.slug}</code>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem' }}>
          <button type="button" style={{ ...btnTiny, opacity: first ? 0.35 : 1 }} disabled={first}
            onClick={() => onMovePage(page.id, -1)} aria-label="Move page up" title="Move up">
            <i className="bi bi-chevron-up" />
          </button>
          <button type="button" style={{ ...btnTiny, opacity: last ? 0.35 : 1 }} disabled={last}
            onClick={() => onMovePage(page.id, 1)} aria-label="Move page down" title="Move down">
            <i className="bi bi-chevron-down" />
          </button>
          <button type="button" style={btnTiny} onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button type="button" style={{ ...btnTiny, color: brand.red, borderColor: brand.red }} onClick={del}>
            Delete
          </button>
        </div>
      </Card.Header>
      <Card.Body>
        {editing && (
          <div style={{ marginBottom: '0.8rem' }}>
            <PageForm draft={draft} onChange={setDraft} groups={groups} />
            <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem' }}>
              <button type="button" style={btnPrimary} onClick={save}
                disabled={updateState.isLoading || !draft.name.trim()}>
                {updateState.isLoading ? 'Saving…' : 'Save page'}
              </button>
            </div>
            <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.4rem' }}>
              <i className="bi bi-info-circle me-1" />
              Renaming changes the sidebar label. The address stays{' '}
              <code>/resources/{page.slug}</code> — bookmarks keep working and uploaded
              files don&apos;t move.
            </div>
          </div>
        )}

        {page.description && !editing && (
          <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginBottom: '0.4rem' }}>{page.description}</div>
        )}

        {items.length === 0 && (
          <div style={{ color: brand.charcoal, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Nothing on this page yet. Employees see it in the menu with an
            &quot;empty&quot; note until there is.
          </div>
        )}

        {items.map((item, i) => (
          <ItemRow key={item.id} item={item} pages={pages}
            first={i === 0} last={i === items.length - 1}
            onMove={moveItem} onSaved={onSaved} onError={onError} />
        ))}

        <AddItem page={page} onSaved={onSaved} onError={onError} />
      </Card.Body>
    </Card>
  );
}
PageCard.propTypes = {
  page: PropTypes.object.isRequired,
  pages: PropTypes.array,
  groups: PropTypes.array,
  first: PropTypes.bool,
  last: PropTypes.bool,
  onMovePage: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
PageCard.defaultProps = { pages: [], groups: [], first: false, last: false };

/* One folder's heading row, above the page cards filed into it.
 *
 * Renaming is free here in a way it is not on a page: a folder has no slug, so
 * no URL is bookmarked and no S3 prefix is named after it. That is why this row
 * offers a plain name field where a page card shows its address read-only.
 *
 * Deleting is refused by the server while the folder still holds pages, and the
 * button says so rather than letting the 409 be the explanation. An empty
 * folder is already invisible to both audiences — a group's visibility is
 * derived from the pages in it, never stored — so there is nothing to hide in
 * the meantime and no hurry to delete it.
 */
function GroupHeading({ group, first, last, onMove, onSaved, onError }) {
  const [update, updateState] = useUpdateResourceGroupMutation();
  const [remove] = useDeleteResourceGroupMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: group.name || '', icon: group.icon || '' });

  const pageCount = group.page_count || 0;

  const save = async () => {
    try {
      await update({
        id: group.id,
        body: { name: draft.name.trim(), icon: draft.icon || null },
      }).unwrap();
      setEditing(false);
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not save that folder.')); }
  };

  const del = async () => {
    if (!window.confirm(`Delete the "${group.name}" folder?`)) return;
    try {
      await remove({ id: group.id }).unwrap();
      await onSaved();
    } catch (e) { onError(errText(e, 'Could not delete that folder.')); }
  };

  return (
    <div style={{ marginBottom: '0.6rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
        borderBottom: `2px solid ${brand.softGray}`, paddingBottom: '0.35rem' }}>
        <i className={`bi bi-${group.icon || 'folder'}`} style={{ color: brand.goldenrod }} />
        <span style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.95rem' }}>{group.name}</span>
        <Badge bg="secondary" style={{ fontWeight: 600 }}>
          {pageCount} page{pageCount === 1 ? '' : 's'}
        </Badge>
        {pageCount === 0 && (
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            empty — nobody sees this folder yet
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem' }}>
          <button type="button" style={{ ...btnTiny, opacity: first ? 0.35 : 1 }} disabled={first}
            onClick={() => onMove(group.id, -1)} aria-label="Move folder up" title="Move up">
            <i className="bi bi-chevron-up" />
          </button>
          <button type="button" style={{ ...btnTiny, opacity: last ? 0.35 : 1 }} disabled={last}
            onClick={() => onMove(group.id, 1)} aria-label="Move folder down" title="Move down">
            <i className="bi bi-chevron-down" />
          </button>
          <button type="button" style={btnTiny} onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Rename'}
          </button>
          <button type="button"
            style={{ ...btnTiny, color: brand.red, borderColor: brand.red, opacity: pageCount ? 0.35 : 1 }}
            disabled={!!pageCount} onClick={del}
            title={pageCount ? 'Move its pages out first' : 'Delete this folder'}>
            Delete
          </button>
        </div>
      </div>
      {editing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <input style={{ ...inputStyle, width: 'auto', flex: '1 1 14rem' }} placeholder="Folder name"
            value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <select style={{ ...inputStyle, width: 'auto' }} value={draft.icon}
            onChange={(e) => setDraft({ ...draft, icon: e.target.value })}>
            <option value="">Default folder icon</option>
            {ICON_CHOICES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          <button type="button" style={btnPrimary} onClick={save}
            disabled={updateState.isLoading || !draft.name.trim()}>
            {updateState.isLoading ? 'Saving…' : 'Save folder'}
          </button>
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            Renaming a folder is safe — it has no address of its own, so no
            bookmark breaks and no file moves.
          </span>
        </div>
      )}
    </div>
  );
}
GroupHeading.propTypes = {
  group: PropTypes.object.isRequired,
  first: PropTypes.bool,
  last: PropTypes.bool,
  onMove: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
GroupHeading.defaultProps = { first: false, last: false };

// A new page is visible to employees and NOT to the firms. Publishing to an
// external audience should take a deliberate tick, not a default nobody looked
// at on the way past. `group_id: null` means top level — the same default every
// existing page has.
//
// `beta_published: false` too, though for a milder reason: a page nobody asked
// to stage should not arrive in the pilot group's sidebar. Someone writing a
// page for beta first will untick "Visible to employees" and tick this, which
// is the deliberate act the defaults should make them perform.
const emptyPage = {
  name: '', description: '', intro: '', icon: '',
  group_id: null, published: true, b2b_published: false, beta_published: false,
};

export default function MarketingResourceLibrary() {
  const { data, isLoading, refetch } = useGetResourceAdminPagesQuery();
  const [createPage, createState] = useCreateResourcePageMutation();
  const [reorderPages] = useReorderResourcePagesMutation();
  const [createGroup, createGroupState] = useCreateResourceGroupMutation();
  const [reorderGroups] = useReorderResourceGroupsMutation();

  const [showNew, setShowNew] = useState(false);
  const [newDraft, setNewDraft] = useState(emptyPage);
  const [newGroup, setNewGroup] = useState('');
  const [error, setError] = useState(null);

  const pages = useMemo(() => data?.pages || [], [data]);
  const groups = useMemo(() => data?.groups || [], [data]);
  const itemCount = pages.reduce((n, p) => n + (p.item_count || 0), 0);
  // Just the identity of each page, for the "move to another page" dropdowns —
  // passing the full rows would hand every row its siblings' whole item lists.
  const pageOptions = useMemo(
    () => pages.map((p) => ({ id: p.id, name: p.name })), [pages],
  );

  /* The pages split into folders, in the order the sidebar renders them: each
   * folder's pages, then the ones in no folder.
   *
   * This is the ONE definition of that order, and both reorder handlers read it
   * — page positions are one global sequence (see the reorder endpoint), so a
   * nudge inside a folder has to be flattened back against everything else
   * before it is sent. Deriving the flat list from the same buckets the screen
   * renders is what keeps "what I dragged" and "what was saved" the same thing.
   *
   * The last bucket also catches a page whose group_id points at a folder that
   * is gone. There is no FK, so that is possible, and showing it at the top
   * level is how someone finds it to re-file it.
   */
  const buckets = useMemo(() => {
    const ids = new Set(groups.map((g) => g.id));
    return [
      ...groups.map((g) => ({
        group: g, pages: pages.filter((p) => p.group_id === g.id),
      })),
      {
        group: null,
        pages: pages.filter((p) => p.group_id == null || !ids.has(p.group_id)),
      },
    ];
  }, [groups, pages]);

  const submitNew = async () => {
    setError(null);
    try {
      await createPage({
        name: newDraft.name.trim(), description: newDraft.description || null,
        intro: newDraft.intro || null, icon: newDraft.icon || null,
        group_id: newDraft.group_id ?? null,
        published: !!newDraft.published,
        b2b_published: !!newDraft.b2b_published,
        beta_published: !!newDraft.beta_published,
      }).unwrap();
      setNewDraft(emptyPage);
      setShowNew(false);
      await refetch();
    } catch (e) { setError(errText(e, 'Could not create that page.')); }
  };

  const submitNewGroup = async () => {
    const name = newGroup.trim();
    if (!name) return;
    setError(null);
    try {
      await createGroup({ name }).unwrap();
      setNewGroup('');
      await refetch();
    } catch (e) { setError(errText(e, 'Could not create that folder.')); }
  };

  // A page moves within its OWN folder — up and down reorder siblings, they
  // don't carry a page across a folder boundary. Changing folder is the picker
  // in the page's own form, which is a deliberate act rather than something
  // that happens by nudging a page one row too far.
  const movePage = async (pageId, delta) => {
    const bucket = buckets.find((b) => b.pages.some((p) => p.id === pageId));
    if (!bucket) return;
    const within = swapped(bucket.pages.map((p) => p.id), pageId, delta);
    if (!within) return;
    // Splice the reordered bucket back into the global sequence and send all of
    // it — per-folder sequences would each restart at 10.
    const ordered = buckets.flatMap((b) => (
      b === bucket ? within : b.pages.map((p) => p.id)
    ));
    setError(null);
    try {
      await reorderPages({ ordered_ids: ordered }).unwrap();
      await refetch();
    } catch (e) { setError(errText(e, 'Could not reorder those pages.')); }
  };

  const moveGroup = async (groupId, delta) => {
    const ids = swapped(groups.map((g) => g.id), groupId, delta);
    if (!ids) return;
    setError(null);
    try {
      await reorderGroups({ ordered_ids: ids }).unwrap();
      await refetch();
    } catch (e) { setError(errText(e, 'Could not reorder those folders.')); }
  };

  return (
    <AdminPage
      section="Marketing"
      icon="megaphone"
      title="Resource Library"
      subtitle="Reference pages every employee sees under Desk → Resource Library."
      actions={
        <button type="button" style={btnPrimary} onClick={() => setShowNew((v) => !v)}>
          <i className={`bi ${showNew ? 'bi-x' : 'bi-plus'} me-1`} />
          {showNew ? 'Cancel' : 'New page'}
        </button>
      }
    >
      {error && (
        <div style={{ background: 'rgba(192,57,43,0.08)', border: `1px solid ${brand.red}`, color: brand.red, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{error}
        </div>
      )}

      {showNew && (
        <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
          <Card.Header style={headStyle}>New page</Card.Header>
          <Card.Body>
            <PageForm draft={newDraft} onChange={setNewDraft} groups={groups} />
            <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" style={btnPrimary} onClick={submitNew}
                disabled={createState.isLoading || !newDraft.name.trim()}>
                {createState.isLoading ? 'Creating…' : 'Create page'}
              </button>
              <button type="button" style={btnPlain}
                onClick={() => { setShowNew(false); setNewDraft(emptyPage); }}>
                Cancel
              </button>
              <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
                The name sets the address (<code>/resources/…</code>) and the storage
                folder once, and a later rename won&apos;t change them.
              </span>
            </div>
          </Card.Body>
        </Card>
      )}

      {isLoading && (
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
      )}

      {!isLoading && pages.length === 0 && !showNew && (
        <Card style={cardStyle}>
          <Card.Body style={{ color: brand.charcoal, fontSize: '0.9rem' }}>
            No pages yet. Each one you add becomes an entry under Desk → Resource
            Library for every employee, alongside Marketing.
          </Card.Body>
        </Card>
      )}

      {/* Folders, created from one field because that is all a folder is. No
          publish checkboxes: a folder is shown to whoever can see a page inside
          it, so its audience is the union of its pages' and there is nothing
          here to tick. An empty one is invisible to everybody. */}
      {!isLoading && (
        <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
          <Card.Header style={headStyle}>Folders</Card.Header>
          <Card.Body>
            <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginBottom: '0.6rem' }}>
              A folder groups pages under one expandable entry in the sidebar, next
              to Marketing. Pages in no folder stay where they are, listed directly
              under Resource Library. A folder appears for someone only once it
              holds a page they can see, so an empty one is hidden from everyone.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
              <input style={{ ...inputStyle, width: 'auto', flex: '1 1 14rem' }}
                placeholder="New folder name — e.g. Salesforce"
                value={newGroup} onChange={(e) => setNewGroup(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitNewGroup(); }} />
              <button type="button" style={btnPrimary} onClick={submitNewGroup}
                disabled={createGroupState.isLoading || !newGroup.trim()}>
                {createGroupState.isLoading ? 'Adding…' : 'Add folder'}
              </button>
              <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
                Folders can be renamed freely later — file pages into one with the
                Folder picker on each page.
              </span>
            </div>
          </Card.Body>
        </Card>
      )}

      {!isLoading && pages.length > 0 && (
        <div style={{ color: brand.charcoal, fontSize: '0.78rem', marginBottom: '0.6rem' }}>
          {pages.length} page{pages.length === 1 ? '' : 's'} ·{' '}
          {groups.length} folder{groups.length === 1 ? '' : 's'} · {itemCount} item
          {itemCount === 1 ? '' : 's'} · the order here is the order in the sidebar
        </div>
      )}

      {/* Folder by folder, then the pages in none — the same order the sidebar
          renders, so this screen reads as the menu it is editing. The up/down
          arrows on a page move it among its own folder's pages; the Folder
          picker in its form is what moves it to another folder. */}
      {!isLoading && buckets.map(({ group, pages: bucketPages }, bucketIndex) => {
        // An empty trailing "no folder" bucket is nothing to show; an empty
        // FOLDER still gets its heading, because that is how it gets renamed,
        // reordered or deleted.
        if (!group && bucketPages.length === 0) return null;
        return (
          <div key={group ? `group:${group.id}` : 'ungrouped'}
            data-testid={group ? `folder-${group.id}` : 'folder-none'}
            style={{ marginBottom: '1.2rem' }}>
            {group ? (
              <GroupHeading group={group} first={bucketIndex === 0}
                last={bucketIndex === groups.length - 1}
                onMove={moveGroup} onSaved={refetch} onError={setError} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                borderBottom: `2px solid ${brand.softGray}`, paddingBottom: '0.35rem',
                marginBottom: '0.6rem' }}>
                <i className="bi bi-list-ul" style={{ color: brand.goldenrod }} />
                <span style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.95rem' }}>
                  In no folder
                </span>
                <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
                  listed directly under Resource Library
                </span>
              </div>
            )}
            {group && bucketPages.length === 0 && (
              <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>
                No pages in this folder yet — pick it in a page&apos;s Folder
                dropdown to move one in.
              </div>
            )}
            {bucketPages.map((p, i) => (
              <PageCard key={p.id} page={p} pages={pageOptions} groups={groups}
                first={i === 0} last={i === bucketPages.length - 1}
                onMovePage={movePage} onSaved={refetch} onError={setError} />
            ))}
          </div>
        );
      })}
    </AdminPage>
  );
}
